import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type { ApiClient } from '../services/api-client.service';
import type { PaginationResponse, UseListParams, PaginatedData } from '../types/pagination.types';

type Id = string | number;

export interface ApiHookConfig {
  basePath: string | (() => string);
  queryKey: string[];
  apiClient: ApiClient;
}

// Generic hooks for custom URL endpoints
export const useGenericGet = <TResponse>(
  url: string,
  queryKey: string[],
  apiClient: ApiClient,
  queryOptions?: UseQueryOptions<TResponse>
) => {
  return useQuery<TResponse>({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.get<TResponse>(url);
      return response.data;
    },
    ...queryOptions,
  });
};

export const useGenericPost = <TResponse, TData>(
  url: string,
  queryKey: string[],
  apiClient: ApiClient,
  mutationOptions?: UseMutationOptions<TResponse, unknown, TData>
) => {
  const queryClient = useQueryClient();

  // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
  const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

  return useMutation<TResponse, unknown, TData, unknown>({
    ...restMutationOptions,
    mutationFn: async (data) => {
      const response = await apiClient.post<TResponse>(url, data);
      return response.data;
    },
    onSuccess: (data, variables, context, onMutateResult) => {
      // Invalidate queries with the given queryKey
      queryClient.invalidateQueries({ queryKey });

      // Call the user-provided onSuccess if it exists
      if (userOnSuccess) {
        userOnSuccess(data, variables, context, onMutateResult);
      }
    },
  });
};

export const useGenericPut = <TResponse, TData>(
  url: string,
  queryKey: string[],
  apiClient: ApiClient,
  mutationOptions?: UseMutationOptions<TResponse, unknown, TData>
) => {
  const queryClient = useQueryClient();

  // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
  const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

  return useMutation<TResponse, unknown, TData, unknown>({
    ...restMutationOptions,
    mutationFn: async (data) => {
      const response = await apiClient.put<TResponse>(url, data);
      return response.data;
    },
    onSuccess: (data, variables, context, onMutateResult) => {
      // Invalidate queries with the given queryKey
      queryClient.invalidateQueries({ queryKey });

      // Call the user-provided onSuccess if it exists
      if (userOnSuccess) {
        userOnSuccess(data, variables, context, onMutateResult);
      }
    },
  });
};

export const useGenericPostFormData = <TResponse>(
  url: string,
  queryKey: string[],
  apiClient: ApiClient,
  mutationOptions?: UseMutationOptions<TResponse, unknown, FormData>
) => {
  const queryClient = useQueryClient();

  // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
  const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

  return useMutation<TResponse, unknown, FormData, unknown>({
    ...restMutationOptions,
    mutationFn: async (formData) => {
      const response = await apiClient.post<TResponse>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (data, variables, context, onMutateResult) => {
      // Invalidate queries with the given queryKey
      queryClient.invalidateQueries({ queryKey });

      // Call the user-provided onSuccess if it exists
      if (userOnSuccess) {
        userOnSuccess(data, variables, context, onMutateResult);
      }
    },
  });
};

export const useGenericPutFormData = <TResponse>(
  url: string,
  queryKey: string[],
  apiClient: ApiClient,
  mutationOptions?: UseMutationOptions<TResponse, unknown, FormData>
) => {
  const queryClient = useQueryClient();

  // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
  const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

  return useMutation<TResponse, unknown, FormData, unknown>({
    ...restMutationOptions,
    mutationFn: async (formData) => {
      const response = await apiClient.put<TResponse>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (data, variables, context, onMutateResult) => {
      // Invalidate queries with the given queryKey
      queryClient.invalidateQueries({ queryKey });

      // Call the user-provided onSuccess if it exists
      if (userOnSuccess) {
        userOnSuccess(data, variables, context, onMutateResult);
      }
    },
  });
};

export const createApiHooks = <TList, TDetail, TCreate, TUpdate>(config: ApiHookConfig) => {
  const { basePath, queryKey, apiClient } = config;

  // Helper function to get the current basePath value
  const getBasePath = () => {
    return typeof basePath === 'function' ? basePath() : basePath;
  };

  // List
  const useList = (
    params?: UseListParams,
    queryOptions?: UseQueryOptions<PaginatedData<TList>>
  ) => {
    const queryClient = useQueryClient();

    return useQuery<PaginatedData<TList>>({
      queryKey: [queryKey, 'list', params],
      queryFn: async () => {
        // Start with base URL
        let url = getBasePath();
        const queryParams = new URLSearchParams();

        // Add pagination params
        if (params?.page !== undefined) queryParams.append('page', params.page.toString());
        if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());

        // Add search parameter if present
        if (params?.searchString) {
          queryParams.append('searchString', params.searchString);
        }

        // Add sorting parameters if present
        if (params?.sortField) {
          queryParams.append('sortField', params.sortField);
          // Only append sortOrder if sortField is provided
          if (params?.sortOrder) {
            queryParams.append('sortOrder', params.sortOrder);
          }
        }

        // Add filters if present
        if (params?.filters) {
          // If filters is already a string, use it directly
          if (typeof params.filters === 'string') {
            // Split the filter string by '&' and add each part separately
            params.filters.split('&').forEach((filter) => {
              const [key, value] = filter.split('=');
              if (key && value) {
                queryParams.append(key, value);
              }
            });
          }
        }

        const queryString = queryParams.toString();
        if (queryString) {
          url = `${url}?${queryString}`;
        }

        const response = await apiClient.get<PaginationResponse<TList>>(url);
        return response.data.data;
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      ...queryOptions,
    });
  };

  // Get
  const useGet = (id: Id, queryOptions?: UseQueryOptions<TDetail>) => {
    const queryClient = useQueryClient();

    return useQuery<TDetail>({
      queryKey: [queryKey, 'detail', id],
      queryFn: async () => {
        const response = await apiClient.get<TDetail>(`${getBasePath()}/${id}`);
        return response.data;
      },
      enabled: !!id,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      ...queryOptions,
    });
  };

  // Create
  const useCreate = (mutationOptions?: UseMutationOptions<TDetail, unknown, TCreate>) => {
    const queryClient = useQueryClient();

    // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
    const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

    return useMutation<TDetail, unknown, TCreate, unknown>({
      ...restMutationOptions,
      mutationFn: async (data) => {
        const response = await apiClient.post<TDetail>(getBasePath(), data);
        return response.data;
      },
      onSuccess: (data, variables, context, onMutateResult) => {
        // Always invalidate the list query
        queryClient.invalidateQueries({ queryKey: [queryKey, 'list'] });

        // Call the user-provided onSuccess if it exists
        if (userOnSuccess) {
          userOnSuccess(data, variables, context, onMutateResult);
        }
      },
    });
  };

  // Update
  const useUpdate = (
    mutationOptions?: UseMutationOptions<TDetail, unknown, { id: Id; data: TUpdate }>
  ) => {
    const queryClient = useQueryClient();

    // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
    const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

    return useMutation<TDetail, unknown, { id: Id; data: TUpdate }, unknown>({
      ...restMutationOptions,
      mutationFn: async ({ id, data }) => {
        const response = await apiClient.put<TDetail>(`${getBasePath()}/${id}`, data);
        return response.data;
      },
      onSuccess: (data, variables, context, onMutateResult) => {
        // Always invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: [queryKey, 'list'] });
        queryClient.invalidateQueries({
          queryKey: [queryKey, 'detail', variables.id],
        });

        // Call the user-provided onSuccess if it exists
        if (userOnSuccess) {
          userOnSuccess(data, variables, context, onMutateResult);
        }
      },
    });
  };

  // Delete
  const useDelete = (mutationOptions?: UseMutationOptions<void, unknown, Id>) => {
    const queryClient = useQueryClient();

    // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
    const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

    return useMutation<void, unknown, Id, unknown>({
      ...restMutationOptions,
      mutationFn: async (id) => {
        await apiClient.delete(`${getBasePath()}/${id}`);
      },
      onSuccess: (data, variables, context, onMutateResult) => {
        // Always invalidate and remove relevant queries
        queryClient.invalidateQueries({ queryKey: [queryKey, 'list'] });
        queryClient.removeQueries({
          queryKey: [queryKey, 'detail', variables],
        });

        // Call the user-provided onSuccess if it exists
        if (userOnSuccess) {
          userOnSuccess(data, variables, context, onMutateResult);
        }
      },
    });
  };

  // Create with FormData
  const useCreateFormData = (mutationOptions?: UseMutationOptions<TDetail, unknown, FormData>) => {
    const queryClient = useQueryClient();

    // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
    const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

    return useMutation<TDetail, unknown, FormData, unknown>({
      ...restMutationOptions,
      mutationFn: async (formData) => {
        const response = await apiClient.post<TDetail>(getBasePath(), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      },
      onSuccess: (data, variables, context, onMutateResult) => {
        // Always invalidate the list query
        queryClient.invalidateQueries({ queryKey: [queryKey, 'list'] });

        // Call the user-provided onSuccess if it exists
        if (userOnSuccess) {
          userOnSuccess(data, variables, context, onMutateResult);
        }
      },
    });
  };

  // Update with FormData
  const useUpdateFormData = (
    mutationOptions?: UseMutationOptions<TDetail, unknown, { id: Id; formData: FormData }>
  ) => {
    const queryClient = useQueryClient();

    // Extract onSuccess from mutationOptions to prevent it from overriding our implementation
    const { onSuccess: userOnSuccess, ...restMutationOptions } = mutationOptions || {};

    return useMutation<TDetail, unknown, { id: Id; formData: FormData }, unknown>({
      ...restMutationOptions,
      mutationFn: async ({ id, formData }) => {
        const response = await apiClient.put<TDetail>(`${getBasePath()}/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      },
      onSuccess: (data, variables, context, onMutateResult) => {
        // Always invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: [queryKey, 'list'] });
        queryClient.invalidateQueries({
          queryKey: [queryKey, 'detail', variables.id],
        });

        // Call the user-provided onSuccess if it exists
        if (userOnSuccess) {
          userOnSuccess(data, variables, context, onMutateResult);
        }
      },
    });
  };

  return {
    useList,
    useGet,
    useCreate,
    useUpdate,
    useDelete,
    useCreateFormData,
    useUpdateFormData,
  };
};
